import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { logger } from '../utils';

// Vite names the entry chunk after a hash of the whole build, so the
// `index-<hash>.js` that index.html points at changes with every code change
// and only then: a backend-only deploy never asks anyone to reload.
const ENTRY_SCRIPT = /\/assets\/index-[\w-]+\.js/;
export const VERSION_CHECK_INTERVAL_MS = 60_000;
// A new build must still be served this long after it was first seen before
// the tab trusts it (see useVersionCheck).
export const VERSION_CONFIRM_DELAY_MS = 45_000;

export function entryScriptOf(text) {
  return text.match(ENTRY_SCRIPT)?.[0] ?? null;
}

export function runningEntryScript(doc = document) {
  for (const script of doc.querySelectorAll('script[type="module"][src]')) {
    const entry = entryScriptOf(new URL(script.src, window.location.href).pathname);
    if (entry) return entry;
  }
  return null;
}

function hasUnsentText() {
  return [...document.querySelectorAll('textarea')].some((field) => field.value.trim());
}

/**
 * Notice a deploy in an open tab and move the tab onto it.
 *
 * A single-page app never refetches index.html, so a tab opened before a
 * deploy kept running the old code for hours (2026-09-21: a First Label poll
 * fixed that morning was still firing every 5 s from an old tab). The tab
 * compares its own entry script with the one the server's index.html names,
 * every minute and whenever it regains focus. Once they differ it reloads at
 * a moment the user is not interrupted: on the next route change, or when
 * the tab is hidden and no unsent text would be lost. Until then `stale`
 * lets the layout offer a manual reload.
 *
 * A new build counts only once two consecutive checks, at least
 * VERSION_CONFIRM_DELAY_MS apart, both see it. During a rolling deploy the
 * old and the new frontend containers are briefly both behind the proxy: a
 * single sighting could come from the new one while the reload that follows
 * is served by the old one, and the user stays on the old code (2026-09-22).
 */
export function useVersionCheck() {
  const running = useRef(runningEntryScript());
  // The new entry script seen by the last check, and when it was first seen.
  const candidate = useRef(null);
  const [stale, setStale] = useState(false);
  const { pathname } = useLocation();
  const lastPathname = useRef(pathname);

  const check = useCallback(async () => {
    // The dev server serves /src/main.jsx, which has no build hash to compare.
    if (!running.current) return;
    try {
      const response = await fetch('/index.html', { cache: 'no-store' });
      if (!response.ok) {
        logger.warn('Version check got', response.status);
        return;
      }
      const latest = entryScriptOf(await response.text());
      if (!latest || latest === running.current) {
        candidate.current = null;
        return;
      }
      if (candidate.current?.entry !== latest) {
        candidate.current = { entry: latest, seenAt: Date.now() };
        return;
      }
      if (Date.now() - candidate.current.seenAt >= VERSION_CONFIRM_DELAY_MS) setStale(true);
    } catch (error) {
      // Offline or mid-deploy: the next check retries.
      logger.warn('Version check failed', error);
    }
  }, []);

  const reload = useCallback(() => window.location.reload(), []);

  useEffect(() => {
    const interval = window.setInterval(check, VERSION_CHECK_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
      else if (stale && !hasUnsentText()) reload();
    };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [check, reload, stale]);

  useEffect(() => {
    if (pathname === lastPathname.current) return;
    lastPathname.current = pathname;
    if (stale) reload();
  }, [pathname, stale, reload]);

  return { stale, reload };
}
